
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ProductTour from 'src/components/docs/ProductTour/ProductTour';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Link Recovery (beta) allows you to track the status of institution downtimes and notify users when they have been resolved'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Link Recovery (beta)' subtitle='Notify users impacted by institution downtime to return to your app and link their accounts' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <Callout mdxType="Callout">
      <p>{`Link Recovery is currently in closed beta and not accepting new customers. Expanded access to Link Recovery is anticipated in H2 2026.`}</p>
    </Callout>
    <p>{`Link Recovery (beta) allows you to provide a superior user experience and improved conversion when users run into issues connecting with an institution. When a user attempts to connect an institution that is experiencing a temporary connectivity outage, Link Recovery lets the user opt in to receiving an email notification from Plaid letting them know when the issue has been resolved. The user can then launch Link directly from a link in the email notification and complete the process of connecting their account. `}</p>
    <p>{`Link Recovery is designed to be easy to implement and can be added to an existing Plaid integration with only a few lines of code.`}</p>
    <ProductTour steps={[{
      text: 'The user encounters an error because their institution is down.',
      image: '/assets/img/docs/link-recovery/lr-1.png'
    }, {
      text: 'They elect to be notified when the problem is resolved.',
      image: '/assets/img/docs/link-recovery/lr-2.png'
    }, {
      text: 'They exit Link...',
      image: '/assets/img/docs/link-recovery/lr-3.png'
    }, {
      text: '...and receive a confirmation email.',
      image: '/assets/img/docs/link-recovery/lr-4.png'
    }, {
      text: 'When the institution is back up, they are notified...',
      image: '/assets/img/docs/link-recovery/return-1.png'
    }, {
      text: "...and can click a link in the email to go directly to Link.",
      image: '/assets/img/docs/link-recovery/return-2.png'
    }, {
      text: 'They connect to the institution...',
      image: '/assets/img/docs/link-recovery/return-3.png'
    }, {
      text: '...select their account...',
      image: '/assets/img/docs/link-recovery/return-4.png'
    }, {
      text: '...and receive a success screen with a button to redirect them to your app.',
      image: '/assets/img/docs/link-recovery/return-5.png'
    }]} mdxType="ProductTour" />
    <h4 {...{
      "id": "integration-steps"
    }}>{`Integration steps`}</h4>
    <h5 {...{
      "id": "calling-linktokencreate"
    }}>{`Calling /link/token/create`}</h5>
    <p>{`When calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, specify the following, in addition to your normal parameters:`}</p>
    <ul>
      <li parentName="ul">{`A `}<inlineCode parentName="li">{`webhook`}</inlineCode>{` URI, where you will receive the `}<inlineCode parentName="li">{`SESSION_FINISHED`}</inlineCode>{` webhook used to deliver the `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` for Link Recovery sessions.`}</li>
      <li parentName="ul">{`(Optional) A `}<inlineCode parentName="li">{`hosted_link.completion_redirect_uri`}</inlineCode>{`, indicating the URI that the user should be redirected to after completing a Link Recovery session. This field is optional, but strongly recommended; you can use it to redirect the user to your app so they can see the impact of linking their account.`}</li>
      <li parentName="ul">{`If you are not specifying a `}<inlineCode parentName="li">{`hosted_link.completion_redirect_uri`}</inlineCode>{`, include a `}<inlineCode parentName="li">{`hosted_link`}</inlineCode>{` object (an empty object is fine) in the request in order to enable Hosted Link for your session. Link Recovery requires Hosted Link.`}</li>
    </ul>
    <h5 {...{
      "id": "listening-for-the-session_finished-webhook"
    }}>{`Listening for the SESSION_FINISHED webhook`}</h5>
    <p>{`Next, make sure to listen for the `}<inlineCode parentName="p">{`SESSION_FINISHED`}</inlineCode>{` webhook, which will fire when your end user completes their Link Recovery session.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Sample SESSION_FINISHED webhook",
        "switcher": "false",
        "#Sample": true,
        "SESSION_FINISHED": true,
        "webhook": true
      }}>{`{
  "webhook_type": "LINK",
  "webhook_code": "SESSION_FINISHED",
  "status": "success",
  "link_session_id": "356dbb28-7f98-44d1-8e6d-0cec580f3171",
  "link_token": "link-sandbox-af1a0311-da53-4636-b754-dd15cc058176",
  "public_tokens": [
    "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"
  ],
  "environment": "sandbox"
}
`}</code></pre>
    <p>{`After retrieving the public token from the `}<inlineCode parentName="p">{`public_tokens`}</inlineCode>{` array, you can exchange it as normal.`}</p>
    <h4 {...{
      "id": "testing-link-recovery"
    }}>{`Testing Link Recovery`}</h4>
    <p>{`Plaid will not send email notifications in the Sandbox environment, so the Link Recovery flow cannot be tested end-to-end in Sandbox. Instead, you can test the two parts of the flow separately.`}</p>
    <p>{`To test that you are enabled for Link Recovery and that users can sign up to receive emails, log in to Link in Sandbox with the following credentials to simulate a temporary institution outage: username: `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` password: `}<inlineCode parentName="p">{`error_INSTITUTION_NOT_RESPONDING`}</inlineCode>{`. (If you are not yet enabled for Link Recovery, you can also use the following credentials to see the Link Recovery experience in Sandbox: username: `}<inlineCode parentName="p">{`user_link_recovery`}</inlineCode>{` password: `}<inlineCode parentName="p">{`{}`}</inlineCode>{`.) `}</p>
    <p>{`When prompted for an email address in the Sandbox Link Recovery flow, enter `}<inlineCode parentName="p">{`example@plaid.com`}</inlineCode>{` as the email and `}<inlineCode parentName="p">{`123456`}</inlineCode>{` as the security code. Note that in the Sandbox environment, no Link Recovery emails will actually be sent.`}</p>
    <p>{`To test that the Link Recovery session works correctly, call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` and manually go to the `}<inlineCode parentName="p">{`hosted_link_url`}</inlineCode>{` present in the response. This will launch a Hosted Link session, where you can test going through the Link flow with the `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` / `}<inlineCode parentName="p">{`pass_good`}</inlineCode>{` credentials and triggering the `}<inlineCode parentName="p">{`SESSION_FINISHED`}</inlineCode>{` webhook.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-steps",
        level: 4,
        title: "Integration steps",
      },
{
        id: "testing-link-recovery",
        level: 4,
        title: "Testing Link Recovery",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
